import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { IconArrowDown, IconArrowUp } from '../svg/IconSvg'
import { ToggleRadioButton } from '../input/InputRadioButton'
import { BtnFixed } from '../Button'
import { plusComma } from '../../js/script'

import { PaymentInfo } from '@shopby/shop-sdk'

import { useCommonContext } from '../provider/CommonProvider'
import PrivacyInfoCollectionAgree from './PrivacyInfoCollectionAgree'

interface OrderSummarySectionProps {
  paymentInfo: PaymentInfo
  accumulationUseAmt: number
  selectPayType: string
  isAgreePayment: boolean
  setIsAgreePayment: (v: boolean) => void
  isAgreeNonMemberPrivacy: boolean
  setIsAgreeNonMemberPrivacy: (v: boolean) => void
  showAgreement: boolean
  setShowAgreement: React.Dispatch<React.SetStateAction<boolean>>
  onPurchase: () => void
}

const OrderSummarySection = ({
  paymentInfo,
  accumulationUseAmt,
  selectPayType,
  isAgreePayment,
  setIsAgreePayment,
  isAgreeNonMemberPrivacy,
  setIsAgreeNonMemberPrivacy,
  showAgreement,
  setShowAgreement,
  onPurchase,
}: OrderSummarySectionProps) => {
  const [loading, setLoading] = useState(false)

  // 내부에서만 사용하는 상태만 유지
  const [openDiscount, setOpenDiscount] = useState<boolean>(false)
  const [productCouponDiscountAmount, setProductCouponDiscountAmount] = useState<number>(0)
  const [cartCouponDiscountAmount, setCartCouponDiscountAmount] = useState<number>(0)
  const [productDiscountAmount, setProductDiscountAmount] = useState<number>(0)
  const [deliveryAmount, setDeliveryAmount] = useState<number>(0)
  const [productAmount, setProductAmount] = useState<number>(0)
  const [paymentAmount, setPaymentAmount] = useState<number>(0)

  const { hasAccessToken } = useCommonContext()

  useEffect(() => {
    if (!paymentInfo) return

    setOpenDiscount(false) // 초기 로딩 후 할인 정보 닫기
    setLoading(true)

    const productCouponDiscount = paymentInfo?.productCouponAmt || 0
    const cartCouponDiscount = paymentInfo?.cartCouponAmt || 0
    const productDiscount =
      (paymentInfo?.totalImmediateDiscountAmt || 0) + (paymentInfo?.totalAdditionalDiscountAmt || 0)
    const deliveryAmt = (paymentInfo?.deliveryAmt || 0) + (paymentInfo?.remoteDeliveryAmt || 0)
    setProductDiscountAmount(productDiscount)
    setProductCouponDiscountAmount(productCouponDiscount)
    setCartCouponDiscountAmount(cartCouponDiscount)
    setDeliveryAmount(deliveryAmt)
    setProductAmount(paymentInfo?.totalStandardAmt || 0)
    setPaymentAmount(paymentInfo?.paymentAmt || 0)

    const timer = setTimeout(() => {
      setLoading(false)
    }, 800)
    return () => clearTimeout(timer)
  }, [paymentInfo, accumulationUseAmt])

  return (
    <>
      <div className='right_area_info'>
        <Hr8 />
        <SectionDiv>
          <SectionTitle>결제 정보</SectionTitle>
          <div className='payment_info_wrap'>
            <ul>
              <li>
                <dl>
                  <dt>전체 상품 금액</dt>
                  <dd>
                    {loading ? (
                      <SpinnerContainer>
                        <LoadingSpinner $duration={0.7} />
                      </SpinnerContainer>
                    ) : (
                      <>{plusComma(productAmount)}원</>
                    )}
                  </dd>
                </dl>
              </li>
              <li>
                <div className='discount_wrap'>
                  <dl onClick={() => setOpenDiscount((prev) => !prev)}>
                    <dt>
                      할인금액 합계
                      {openDiscount ? (
                        <IconArrowUp width={16} height={16} color='var(--colPointOrange)' />
                      ) : (
                        <IconArrowDown width={16} height={16} color='var(--colPointOrange)' />
                      )}
                    </dt>
                    <dd>
                      {loading ? (
                        <SpinnerContainer>
                          <LoadingSpinner $duration={1.2} />
                        </SpinnerContainer>
                      ) : (
                        <>
                          -{' '}
                          {plusComma(
                            Number(paymentInfo?.totalImmediateDiscountAmt) +
                              Number(accumulationUseAmt) +
                              Number(paymentInfo?.cartCouponAmt) +
                              Number(paymentInfo?.productCouponAmt)
                          )}
                          원
                        </>
                      )}
                    </dd>
                  </dl>
                </div>
                <ul className={openDiscount ? 'discount_detail_list on' : 'discount_detail_list'}>
                  <li>
                    <dl>
                      <dt>⎿ 상품즉시 할인 합계</dt>
                      <dd>
                        {productDiscountAmount > 0 && '- '}
                        {plusComma(productDiscountAmount)}원
                      </dd>
                    </dl>
                  </li>
                  {hasAccessToken && (
                    <li>
                      <dl>
                        <dt>⎿ 상품 쿠폰할인 합계</dt>
                        <dd>
                          {productCouponDiscountAmount > 0 && '- '}
                          {plusComma(productCouponDiscountAmount)}원
                        </dd>
                      </dl>
                    </li>
                  )}
                  {hasAccessToken && (
                    <li>
                      <dl>
                        <dt>⎿ 장바구니 쿠폰할인</dt>
                        <dd>
                          {cartCouponDiscountAmount > 0 && '- '}
                          {plusComma(cartCouponDiscountAmount)}원
                        </dd>
                      </dl>
                    </li>
                  )}
                  {hasAccessToken && (
                    <li>
                      <dl>
                        <dt>⎿ 적립금 할인 합계</dt>
                        <dd>
                          {accumulationUseAmt > 0 && '- '}
                          {plusComma(accumulationUseAmt)}원
                        </dd>
                      </dl>
                    </li>
                  )}
                </ul>
              </li>
              <li>
                <dl>
                  <dt>배송비</dt>
                  <dd>
                    {loading ? (
                      <SpinnerContainer>
                        <LoadingSpinner $duration={1.2} />
                      </SpinnerContainer>
                    ) : (
                      <>+ {plusComma(deliveryAmount)}원</>
                    )}
                  </dd>
                </dl>
              </li>
            </ul>
          </div>
          <div className='total_infoWrap'>
            <dl className='total_payment_wrap'>
              <dt>총 결제 금액</dt>
              <dd>
                {loading ? (
                  <SpinnerContainer>
                    <LoadingSpinner $duration={2} />
                  </SpinnerContainer>
                ) : (
                  <>{plusComma(paymentAmount)}원</>
                )}
              </dd>
            </dl>
            {hasAccessToken && paymentInfo?.accumulationAmtWhenBuyConfirm !== 0 && (
              <p className='mileage'>
                구매확정 시 페이백{' '}
                {selectPayType === 'MY_PAY'
                  ? plusComma(
                      Number(paymentInfo?.accumulationAmtWhenBuyConfirm) +
                        (Number(paymentInfo?.totalStandardAmt) - Number(paymentInfo?.totalImmediateDiscountAmt)) * 0.02
                    )
                  : plusComma(Number(paymentInfo?.accumulationAmtWhenBuyConfirm))}
                <img src='/images/icon_won.svg' alt='coin' style={{ width: 14, height: 14 }} />
                적립 예정
              </p>
            )}
          </div>
          <div className='purchase_agree_wrap' style={{ position: 'relative' }}>
            <ToggleRadioButton
              name='agreePayment'
              value='Y'
              checked={isAgreePayment}
              onChange={() => setIsAgreePayment(!isAgreePayment)}
              label='구매조건 확인 및 결제 진행 동의'
              size='small'
            />
            <span
              style={{
                opacity: 0,
                width: '15px',
                height: '15px',
                position: 'absolute',
                right: '0',
                top: '20%',
              }}
            ></span>
            {!hasAccessToken && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <ToggleRadioButton
                  name='agreeNonMemberPrivacy'
                  value='Y'
                  checked={isAgreeNonMemberPrivacy}
                  onChange={() => setIsAgreeNonMemberPrivacy(!isAgreeNonMemberPrivacy)}
                  label='비회원 주문 개인정보 수집 / 이용 동의'
                  size='small'
                />
                <button onClick={() => setShowAgreement(!showAgreement)}>보기</button>
              </div>
            )}
          </div>
        </SectionDiv>
        <div className='purchase_wrap'>
          <BtnFixed onClick={onPurchase} disabled={loading}>
            {loading ? (
              <SpinnerContainer>
                <LoadingSpinner $duration={2} />
              </SpinnerContainer>
            ) : (
              <>{plusComma(paymentAmount)}원 구매하기</>
            )}
          </BtnFixed>
        </div>
      </div>
      {showAgreement && (
        <PrivacyInfoCollectionAgree isModal={showAgreement} setIsModal={setShowAgreement}></PrivacyInfoCollectionAgree>
      )}
    </>
  )
}

const Hr8 = styled.div`
  height: 8px;
  background: var(--colGray1);
  width: 100%;
  display: none;

  @media (max-width: 949px) {
    display: block;
  }
`

const SectionDiv = styled.div`
  padding: 2px 20px 20px 20px;

  @media screen and (max-width: 768px) {
    padding: 32px 20px 32px 20px;
    border-bottom: 1px solid var(--colGray3);
    &:last-child {
      border-bottom: none;
    }
  }
`

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 20px;
`

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

export const LoadingSpinner = styled.div<{ $duration?: number }>`
  @keyframes spinner {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  display: flex;
  margin: auto;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #898989;
  animation: spinner ${({ $duration }) => $duration || 1}s ease infinite;

  @media screen and (min-width: 768px) {
    width: 18px;
    height: 18px;
  }
`

export default OrderSummarySection
